import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDateTime } from "@/lib/utils-ui";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Send, Lock, Trash2, Paperclip } from "lucide-react";
import AttachmentUploader from "@/components/AttachmentUploader";
import ConfirmDialog from "@/components/ConfirmDialog";

const STATUSES = ["open","assigned","in_progress","resolved","closed"];
const PRIORITIES = ["low","medium","high","critical"];

export default function TicketDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [users, setUsers] = useState([]);
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
const [confirmDel, setConfirmDel] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get(`/tickets/${id}`);
    setT(data);
  };
  useEffect(() => { load(); api.get("/auth/users").then(r => setUsers(r.data)).catch(() => {}); }, [id]);

  if (!t) return <div className="p-10 data-label">Loading ticket…</div>;

  const canManage = ["super_admin","company_admin","it_admin"].includes(user?.role);

  const update = async (payload) => {
    await api.patch(`/tickets/${id}`, payload);
    toast.success("Updated");
    load();
  };

  const addComment = async () => {
    if (!body.trim()) return;
    await api.post(`/tickets/${id}/comments`, { body, internal });
    setBody(""); setInternal(false);
    load();
  };

  const rerunAI = async () => {
    setAiLoading(true);
    try {
      await api.post(`/tickets/${id}/ai-analyze`);
      toast.success("AI analysis refreshed");
      load();
    } catch { toast.error("AI unavailable"); }
    finally { setAiLoading(false); }
  };

  const userName = (uid) => users.find(u => u.id === uid)?.full_name || "—";

  const deleteTicket = async () => {
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket deleted");
      nav("/tickets");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex" data-testid="ticket-detail-page">
      <div className="flex-1 p-8 space-y-6 min-w-0">
        <button onClick={() => nav("/tickets")} data-testid="back-btn" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-[#FF5000] flex items-center gap-1">
          <ArrowLeft size={12}/> Back to Support Desk
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-xs text-muted-foreground">{t.ticket_id}</div>
            <h1 className="font-head font-black uppercase text-3xl tracking-tighter mt-1">{t.title}</h1>
            <div className="flex items-center gap-2 mt-3">
              <span className={`text-[10px] uppercase px-2 py-0.5 font-semibold priority-${t.priority}`}>{t.priority}</span>
              <span className={`text-[10px] uppercase px-2 py-0.5 font-semibold status-${t.status}`}>{t.status.replace("_"," ")}</span>
              <span className="data-label">{t.category.replace("_"," ")}</span>
            </div>
          </div>
          {canManage && (
            <button data-testid="delete-ticket-btn" onClick={() => setConfirmDel(true)}
                    className="border border-border h-9 px-3 text-xs uppercase tracking-wider font-head hover:bg-[#FF3B30] hover:text-white inline-flex items-center gap-1">
              <Trash2 size={12}/> Delete
            </button>
          )}
        </div>

        <div className="border border-border bg-white p-5">
          <DataLabel>Description</DataLabel>
          <div className="text-sm mt-2 whitespace-pre-wrap leading-relaxed">{t.description}</div>
        </div>

        <div className="border border-border bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <Paperclip size={14}/>
            <DataLabel>Attachments · {(t.attachments || []).length}</DataLabel>
          </div>
          <AttachmentUploader
            ticketId={t.id}
            attachments={t.attachments || []}
            onUploaded={(updated) => setT(updated)}
            canDelete={canManage}
          />
        </div>

        {/* Comments */}
        <div className="border border-border bg-white">
          <div className="p-4 border-b border-border"><DataLabel>Conversation · {t.comments?.length || 0}</DataLabel></div>
          <div className="p-4 space-y-4 max-h-96 overflow-y-auto" data-testid="comments-list">
            {(t.comments || []).map(c => (
              <div key={c.id} className={`border-l-2 pl-3 ${c.internal ? "border-[#FF5000] bg-[#FFF4EB]/40" : "border-border"}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold">{c.author_name}</span>
                  {c.internal && <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#FF5000]"><Lock size={10}/> Internal</span>}
                  <span className="font-mono text-muted-foreground">{fmtDateTime(c.timestamp)}</span>
                </div>
                <div className="text-sm mt-1 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
            {(t.comments || []).length === 0 && <div className="text-xs text-muted-foreground">No comments yet.</div>}
          </div>
          <div className="border-t border-border p-4 space-y-2">
            <Textarea data-testid="comment-input" rows={3} placeholder="Add a reply…" value={body} onChange={e => setBody(e.target.value)}/>
            <div className="flex items-center justify-between">
              {canManage ? (
                <label className="flex items-center gap-2 text-xs">
                  <input data-testid="internal-toggle" type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)}/>
                  <span className="uppercase tracking-wider">Internal note (IT only)</span>
                </label>
              ) : <span />}
              <Button data-testid="comment-submit" onClick={addComment} className="bg-black text-white hover:bg-[#FF5000] h-9 uppercase text-xs tracking-wider font-head"><Send size={12} className="mr-1"/>Post</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Right rail */}
      <aside className="w-80 border-l border-border bg-[#F9F9F8] p-6 space-y-5 shrink-0" data-testid="ticket-sidebar">
        <div>
          <DataLabel>Status</DataLabel>
          {canManage ? (
            <Select value={t.status} onValueChange={(v) => update({ status: v })}>
              <SelectTrigger data-testid="status-select" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          ) : <div className="font-mono text-sm mt-1">{t.status}</div>}
        </div>

        <div>
          <DataLabel>Priority</DataLabel>
          {canManage ? (
            <Select value={t.priority} onValueChange={(v) => update({ priority: v })}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          ) : <div className="font-mono text-sm mt-1">{t.priority}</div>}
        </div>

        <div>
          <DataLabel>Assigned to</DataLabel>
          {canManage ? (
            <Select value={t.assigned_to || "_none"} onValueChange={(v) => update({ assigned_to: v === "_none" ? null : v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Unassigned"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Unassigned</SelectItem>
                {users.filter(u => ["it_admin","company_admin"].includes(u.role)).map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : <div className="text-sm mt-1">{userName(t.assigned_to)}</div>}
        </div>

        <div>
          <DataLabel>Created</DataLabel>
          <div className="font-mono text-xs mt-1">{fmtDateTime(t.created_at)}</div>
          <div className="data-label mt-2">Reporter</div>
          <div className="text-xs mt-1">{userName(t.created_by)}</div>
        </div>

        {t.sla_due && (
          <div>
            <DataLabel>SLA Due</DataLabel>
            <div className="font-mono text-xs mt-1">{fmtDateTime(t.sla_due)}</div>
          </div>
        )}

        {/* AI Panel */}
        <div className="glass-panel p-4 border" data-testid="ai-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-[#FF5000]"/>
              <div className="font-head font-bold text-xs uppercase tracking-wider">AI Assistant</div>
            </div>
            {canManage && (
              <button data-testid="ai-rerun-btn" onClick={rerunAI} disabled={aiLoading} className="data-label hover:text-[#FF5000] disabled:opacity-50">
                {aiLoading ? "…" : "Refresh"}
              </button>
            )}
          </div>
          <div className="mt-3 space-y-2 text-xs">
            <div><span className="data-label">Suggested category</span><div className="font-mono uppercase mt-1">{t.ai_category || "—"}</div></div>
            <div><span className="data-label">Suggestion</span><div className="mt-1 leading-relaxed">{t.ai_suggestion || "—"}</div></div>
          </div>
        </div>
      </aside>
      <ConfirmDialog
        open={confirmDel}
        onOpenChange={setConfirmDel}
        title="Delete ticket?"
        description={`${t.ticket_id} will be soft-deleted. Audit trail preserved.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={deleteTicket}
        testid="delete-ticket"
      />
    </div>
  );
}
